import type { ColormapCreate } from "@mm/metx-workbench.meteomatics.com";

export type LegendStop = {
  value: number;
  color: string;
};

export type LegendColormap = {
  type: "continuous" | "segmented";
  stops: LegendStop[];
  label?: string;
};

/**
 * Type guard for numeric values that are finite (not NaN/Infinity).
 */
const isFiniteNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

/**
 * Normalize/validate the colormap type.
 *
 * @throws Error when the value is not "continuous" or "segmented".
 */
const normalizeType = (type: unknown): LegendColormap["type"] => {
  if (type === "continuous" || type === "segmented") {
    return type;
  }
  throw new Error('Field "type" must be either "continuous" or "segmented".');
};

/**
 * Validate and normalize a raw stops array into legend-friendly stops.
 *
 * - Ensures `stops` is an array of `[number, string]` tuples.
 * - Trims colors.
 * - Requires at least 2 stops.
 * - Sorts stops by ascending value.
 *
 * @throws Error when the stops structure is invalid.
 */
export const normalizeStops = (stops: ColormapCreate["stops"]): LegendStop[] => {
  if (!Array.isArray(stops)) {
    throw new Error('Field "stops" must be an array.');
  }

  const parsed = stops.map((entry, index) => {
    if (!Array.isArray(entry) || entry.length !== 2) {
      throw new Error(`Stop ${index} must be a tuple [value, color].`);
    }

    const [value, color] = entry;
    if (!isFiniteNumber(value)) {
      throw new Error(`Stop ${index} has an invalid number: ${value}.`);
    }

    return { value, color: color.trim() };
  });

  if (parsed.length < 2) {
    throw new Error("At least two stops are required.");
  }

  const sorted = parsed.slice().sort((a, b) => a.value - b.value);
  return sorted;
};

/**
 * Normalize a `ColormapCreate` into a strict `LegendColormap`.
 *
 * @throws Error when the colormap type or stops are invalid.
 */
export const normalizeColormap = (colormap: ColormapCreate): LegendColormap => {
  const type = normalizeType(colormap.type);
  const stops = normalizeStops(colormap.stops);
  return { type, stops, label: colormap.label };
};

/**
 * Compute the numeric domain of a (normalized) legend colormap.
 *
 * Assumes stops are sorted by value (as produced by `normalizeStops`).
 */
export const domainOf = (colormap: LegendColormap) => {
  const values = colormap.stops.map((stop) => stop.value);
  return { min: values[0], max: values[values.length - 1] };
};
